import React, { useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export const HomeSectionForm = ({ onSubmit, onCancel, initialData = null }) => {
  const [activeLanguage, setActiveLanguage] = useState('es');
  const [formData, setFormData] = useState({
    title_es: '',
    title_ca: '',
    title_en: '',
    description_es: '',
    description_ca: '',
    description_en: '',
    left_image: {
      url: '',
      description: '',
      link: '',
      link_text: ''
    },
    right_image: {
      url: '',
      description: '',
      link: '',
      link_text: ''
    },
    active: true
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        left_image: initialData.left_image || { url: '', description: '', link: '', link_text: '' },
        right_image: initialData.right_image || { url: '', description: '', link: '', link_text: '' }
      });
    }
  }, [initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.title_es || !formData.description_es) {
      toast.error('Título y descripción en español son obligatorios');
      return;
    }
    
    onSubmit(formData);
  };

  const updateImage = (side, field, value) => {
    setFormData({
      ...formData,
      [side]: {
        ...formData[side],
        [field]: value
      }
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Language Tabs */}
      <div className="flex gap-2 border-b">
        <button
          type="button"
          onClick={() => setActiveLanguage('es')}
          className={`px-4 py-2 font-medium ${
            activeLanguage === 'es'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-slate-600 hover:text-slate-800'
          }`}
        >
          🇪🇸 Español *
        </button>
        <button
          type="button"
          onClick={() => setActiveLanguage('ca')}
          className={`px-4 py-2 font-medium ${
            activeLanguage === 'ca'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-slate-600 hover:text-slate-800'
          }`}
        >
          🇨🇦 Català
        </button>
        <button
          type="button"
          onClick={() => setActiveLanguage('en')}
          className={`px-4 py-2 font-medium ${
            activeLanguage === 'en'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-slate-600 hover:text-slate-800'
          }`}
        >
          🇬🇧 English
        </button>
      </div>

      {/* Spanish */}
      {activeLanguage === 'es' && (
        <>
          <div>
            <label className="block text-sm font-medium mb-2">Título (Español) *</label>
            <Input
              value={formData.title_es}
              onChange={(e) => setFormData({ ...formData, title_es: e.target.value })}
              placeholder="Ej: InsightFlow"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Descripción (Español) *</label>
            <Textarea
              value={formData.description_es}
              onChange={(e) => setFormData({ ...formData, description_es: e.target.value })}
              placeholder="Texto descriptivo en español"
              rows={4}
              required
            />
          </div>
        </>
      )}

      {/* Catalan */}
      {activeLanguage === 'ca' && (
        <>
          <div>
            <label className="block text-sm font-medium mb-2">Títol (Català)</label>
            <Input
              value={formData.title_ca}
              onChange={(e) => setFormData({ ...formData, title_ca: e.target.value })}
              placeholder="Títol en català"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Descripció (Català)</label>
            <Textarea
              value={formData.description_ca}
              onChange={(e) => setFormData({ ...formData, description_ca: e.target.value })}
              placeholder="Text descriptiu en català"
              rows={4}
            />
          </div>
        </>
      )}

      {/* English */}
      {activeLanguage === 'en' && (
        <>
          <div>
            <label className="block text-sm font-medium mb-2">Title (English)</label>
            <Input
              value={formData.title_en}
              onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
              placeholder="Title in English"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Description (English)</label>
            <Textarea
              value={formData.description_en}
              onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
              placeholder="Descriptive text in English"
              rows={4}
            />
          </div>
        </>
      )}

      {/* Left Image */}
      <div className="border rounded-lg p-4 bg-slate-50">
        <h3 className="font-medium mb-3">Imagen Izquierda</h3>
        <div className="space-y-3">
          <Input
            value={formData.left_image.url}
            onChange={(e) => updateImage('left_image', 'url', e.target.value)}
            placeholder="URL de la imagen"
          />
          <Input
            value={formData.left_image.description}
            onChange={(e) => updateImage('left_image', 'description', e.target.value)}
            placeholder="Descripción de la imagen"
          />
          <Input
            value={formData.left_image.link}
            onChange={(e) => updateImage('left_image', 'link', e.target.value)}
            placeholder="Enlace (opcional)"
          />
          <Input
            value={formData.left_image.link_text}
            onChange={(e) => updateImage('left_image', 'link_text', e.target.value)}
            placeholder="Texto del enlace (opcional)"
          />
        </div>
      </div>

      {/* Right Image */}
      <div className="border rounded-lg p-4 bg-slate-50">
        <h3 className="font-medium mb-3">Imagen Derecha</h3>
        <div className="space-y-3">
          <Input
            value={formData.right_image.url}
            onChange={(e) => updateImage('right_image', 'url', e.target.value)}
            placeholder="URL de la imagen"
          />
          <Input
            value={formData.right_image.description}
            onChange={(e) => updateImage('right_image', 'description', e.target.value)}
            placeholder="Descripción de la imagen"
          />
          <Input
            value={formData.right_image.link}
            onChange={(e) => updateImage('right_image', 'link', e.target.value)}
            placeholder="Enlace (opcional)"
          />
          <Input
            value={formData.right_image.link_text}
            onChange={(e) => updateImage('right_image', 'link_text', e.target.value)}
            placeholder="Texto del enlace (opcional)"
          />
        </div>
      </div>

      {/* Active Toggle */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={formData.active}
          onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
          className="w-4 h-4"
        />
        <label className="text-sm">Sección activa (visible en la home)</label>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <Button type="submit" className="flex-1">
          {initialData ? 'Actualizar' : 'Crear'} Sección
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </form>
  );
};
